import { Classes, Intent, Placement } from "@blueprintjs/core";
import {
  OrgRole,
  OrgRoleRequestGatedAction,
  OrgRoleRequestSource,
  isOrgRoleSuperset,
  orgRoleHasEditPermissions,
} from "@hex/common";
import React, { memo, useCallback, useMemo } from "react";
import { useHistory } from "react-router";
import styled from "styled-components";

import { HexButtonWithDropdownProps } from "../../hex-components/HexButtonWithDropdown.js";
import {
  HexButtonWithDropdown,
  HexMenu,
  HexMenuDivider,
  HexMenuItem,
  HexTooltip,
} from "../../hex-components/index.js";
import { useCurrentUser } from "../../hooks/me/useCurrentUser.js";
import { useOrgExplorerRoleEnabled } from "../../hooks/me/useOrgExplorerRoleEnabled.js";
import { ORG_ID } from "../../orgs";
import { HomeTab, Routes } from "../../route/routes.js";
import CollectionsGateIcon from "../../static/illustration/illustration-featuregate-collections.inline.svg";
import ComponentFeatureGateIcon from "../../static/illustration/illustration-featuregate-components.inline.svg";
import ProjectsGateIcon from "../../static/illustration/illustration-featuregate-projects.inline.svg";
import { useDialog } from "../../util/dialogs.js";
import { HotKeys } from "../../util/hotkeys.js";
import { TestSelectors } from "../../util/testSelectors.js";
import { trackCounterEvent } from "../../util/trackEvent";
import { useEnforceProjectLimit } from "../../util/useEnforceProjectLimit.js";
import { useThreadsEnabledForUser } from "../agent/ask-from-home/hooks/useThreadsEnabledForUser";
import { OrgRoleRequestButton } from "../app/OrgRoleRequestButton.js";
import { ContactAnAdmin } from "../common/ContactAnAdmin.js";
import { useFeatureGates } from "../feature-gate/FeatureGateContext.js";
import { FeatureGateToolTip } from "../feature-gate/FeatureGateToolTip.js";
import {
  AddIcon,
  CollectionIcon,
  ComponentIcon,
  ExploreIcon,
  ProjectIcon,
  RLanguageIcon,
  ThreadIcon,
  UploadIcon,
} from "../icons/CustomIcons.js";
import { ImportNewComponentDialog } from "../upload/ImportComponentDialog";
import { ImportNewProjectDialog } from "../upload/ImportProjectDialog";
import { NewComponentDialog } from "../upload/NewComponentDialog";
import { NewProjectDialog } from "../upload/NewProjectDialog";
import { useCreateNewDefaultComponent } from "../upload/useCreateNewDefaultComponent.js";
import { useCreateNewDefaultExplore } from "../upload/useCreateNewDefaultExplore.js";
import { useCreateNewDefaultProject } from "../upload/useCreateNewDefaultProject.js";

interface CreateNewButtonsProps {
  location?: "home_sidebar" | "popover_sidebar";
  onlyShowRelevantOptions?: boolean;
  selectedTab?: HomeTab;
  buttonPropsOverrides?: Partial<HexButtonWithDropdownProps>;
  placement?: Placement;
}

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  justify-content: flex-end;
  text-wrap: nowrap;
  > .${Classes.POPOVER_TARGET} {
    width: 100%;
  }
`;

export const CreateProjectDialogs = memo(function CreateProjectDialogs() {
  const currentUser = useCurrentUser();
  const orgRole = currentUser?.orgRole;
  const uploadOrCreateDisabled =
    !orgRole ||
    orgRole === OrgRole.GUEST ||
    !orgRoleHasEditPermissions(orgRole);

  return uploadOrCreateDisabled ? null : (
    <>
      <NewProjectDialog />
      <NewComponentDialog />
      <ImportNewProjectDialog />
      <ImportNewComponentDialog />
    </>
  );
});

function isNewTabFromClickEvent(event: React.MouseEvent<HTMLElement>): boolean {
  return event.metaKey || event.ctrlKey;
}

// eslint-disable-next-line max-lines-per-function -- long
export const CreateNewButtons = memo(function CreateNewButtons({
  buttonPropsOverrides,
  location,
  onlyShowRelevantOptions = false,
  placement = "bottom-start",
  selectedTab,
}: CreateNewButtonsProps) {
  const currentUser = useCurrentUser();
  const history = useHistory();
  const exploreRoleEnabled = useOrgExplorerRoleEnabled();
  const orgRole = currentUser?.orgRole;
  const canEditProjectsAndCollections =
    orgRole !== OrgRole.GUEST && orgRoleHasEditPermissions(orgRole);
  const canEditExplorations =
    orgRole && isOrgRoleSuperset(orgRole, OrgRole.EXPLORER);
  const orgRoleCanCreateThreads =
    orgRole && isOrgRoleSuperset(orgRole, OrgRole.EXPLORER);

  const { openDialog: openImportDialog } = useDialog("project-import");
  const { openDialog: openComponentImportDialog } =
    useDialog("component-import");
  const { openDialog: openNewCollectionDialog } = useDialog("new-collection");

  const {
    collections: collectionsFeatureGate,
    components: componentsEnabled,
    exploreFromScratch: exploreFromScratchFeatureGate,
  } = useFeatureGates();

  const { projectLimit, projectLimitEnforced } = useEnforceProjectLimit();

  const createNewThread = useCallback(
    (newTab?: boolean) => {
      if (newTab) {
        window.open(Routes.THREAD.href(ORG_ID, true, {}), "_blank");
      } else {
        history.push(Routes.THREAD.getUrl({}));
      }
    },
    [history],
  );
  const { createPythonProject, createRProject } = useCreateNewDefaultProject();
  const { createPythonComponent, createRComponent } =
    useCreateNewDefaultComponent();
  const createNewExplore = useCreateNewDefaultExplore();
  const { canCreateNewThread } = useThreadsEnabledForUser();

  const newProjectDisabled =
    !canEditProjectsAndCollections || projectLimitEnforced;
  const newComponentDisabled =
    !canEditProjectsAndCollections || !componentsEnabled;
  const newCollectionDisabled =
    !collectionsFeatureGate || !canEditProjectsAndCollections;
  const newExplorationDisabled =
    !canEditExplorations || !exploreFromScratchFeatureGate;

  const popoverContents = useMemo(() => {
    const createProjectItem = (
      <HexMenuItem
        data-test={TestSelectors.CREATE_PROJECT}
        disabled={newProjectDisabled}
        icon={<ProjectIcon />}
        text="New project"
        // eslint-disable-next-line react/jsx-no-bind -- in useMemo
        onClick={(evt) => {
          if (location) {
            trackCounterEvent(`${location}.projects.python.create`);
          }
          void createPythonProject(undefined, isNewTabFromClickEvent(evt));
        }}
      />
    );
    const createComponentItem = (
      <FeatureGateToolTip
        descriptionOverride="Components give you the flexibility to reuse logic across multiple projects from a single source. Upgrade your plan to use components."
        disabled={componentsEnabled}
        featureGate="components"
        icon={<ComponentFeatureGateIcon />}
        placement={placement}
        titleOverride="Upgrade plan to enable components"
      >
        <HexMenuItem
          data-test={TestSelectors.CREATE_COMPONENT}
          disabled={newComponentDisabled}
          icon={<ComponentIcon />}
          text="New component"
          // eslint-disable-next-line react/jsx-no-bind -- in useMemo
          onClick={(evt) => {
            if (location) {
              trackCounterEvent(`${location}.components.python.create`);
            }
            void createPythonComponent(isNewTabFromClickEvent(evt));
          }}
        />
      </FeatureGateToolTip>
    );
    const createCollectionItem = (
      <FeatureGateToolTip
        descriptionOverride="Collections give you the ability to organize and share your projects."
        disabled={collectionsFeatureGate}
        featureGate="collections"
        icon={<CollectionsGateIcon />}
        placement={placement}
        titleOverride="Upgrade plan to enable collections"
      >
        <HexMenuItem
          disabled={newCollectionDisabled}
          icon={<CollectionIcon />}
          text="New collection"
          // eslint-disable-next-line react/jsx-no-bind -- in useMemo
          onClick={() => {
            if (location) {
              trackCounterEvent(`${location}.collection.create`);
            }
            openNewCollectionDialog();
          }}
        />
      </FeatureGateToolTip>
    );
    const createExplorationItem = (
      <FeatureGateToolTip
        action="explore outside of a published app"
        disabled={exploreFromScratchFeatureGate}
        featureGate="exploreFromScratch"
        placement={placement}
      >
        <HexMenuItem
          data-test={TestSelectors.NEW_EXPLORATION}
          disabled={newExplorationDisabled}
          icon={<ExploreIcon />}
          text="New exploration"
          // eslint-disable-next-line react/jsx-no-bind -- in useMemo
          onClick={(evt) => {
            if (location) {
              trackCounterEvent(`${location}.explore.create`);
            }
            void createNewExplore(isNewTabFromClickEvent(evt));
          }}
        />
      </FeatureGateToolTip>
    );
    const createThreadItem = (
      <HexMenuItem
        disabled={!canCreateNewThread}
        icon={<ThreadIcon />}
        text="New thread"
        // eslint-disable-next-line react/jsx-no-bind -- in useMemo
        onClick={(evt) => {
          if (location) {
            trackCounterEvent(`${location}.thread.create`);
          }
          createNewThread(isNewTabFromClickEvent(evt));
        }}
      />
    );

    const createRProjectItem = (
      <HexMenuItem
        data-test={TestSelectors.CREATE_R_PROJECT}
        disabled={newProjectDisabled}
        icon={<RLanguageIcon />}
        text="New R project"
        // eslint-disable-next-line react/jsx-no-bind -- this button rerendering should be harmless
        onClick={(evt) => {
          if (location) {
            trackCounterEvent(`${location}.projects.r.create`);
          }
          void createRProject(undefined, isNewTabFromClickEvent(evt));
        }}
      />
    );
    const createRComponentItem = (
      <FeatureGateToolTip
        descriptionOverride="Components give you the flexibility to reuse logic across multiple projects from a single source. Upgrade your plan to use components."
        disabled={componentsEnabled}
        featureGate="components"
        icon={<ComponentFeatureGateIcon />}
        placement={placement}
        titleOverride="Upgrade plan to enable components"
      >
        <HexMenuItem
          data-test={TestSelectors.CREATE_R_COMPONENT}
          disabled={newComponentDisabled}
          icon={<RLanguageIcon />}
          text="New R component"
          // eslint-disable-next-line react/jsx-no-bind -- in useMemo
          onClick={(evt) => {
            if (location) {
              trackCounterEvent(`${location}.components.r.create`);
            }
            void createRComponent(isNewTabFromClickEvent(evt));
          }}
        />
      </FeatureGateToolTip>
    );
    const importProjectItem = (
      <HexMenuItem
        data-test={TestSelectors.IMPORT_PROJECT}
        disabled={newProjectDisabled}
        icon={<UploadIcon />}
        text="Upload project"
        // eslint-disable-next-line react/jsx-no-bind -- this button rerendering should be harmless
        onClick={() => {
          if (location) {
            trackCounterEvent(`${location}.projects.upload`);
          }
          openImportDialog();
        }}
      />
    );
    const importComponentItem = (
      <FeatureGateToolTip
        descriptionOverride="Components give you the flexibility to reuse logic across multiple projects from a single source. Upgrade your plan to use components."
        disabled={componentsEnabled}
        featureGate="components"
        icon={<ComponentFeatureGateIcon />}
        placement={placement}
        titleOverride="Upgrade plan to enable components"
      >
        <HexMenuItem
          disabled={newComponentDisabled}
          icon={<UploadIcon />}
          text="Upload component"
          // eslint-disable-next-line react/jsx-no-bind -- this button rerendering should be harmless
          onClick={() => {
            if (location) {
              trackCounterEvent(`${location}.components.upload`);
            }
            openComponentImportDialog();
          }}
        />
      </FeatureGateToolTip>
    );

    if (onlyShowRelevantOptions) {
      if (selectedTab === "all") {
        return (
          <HexMenu>
            {createProjectItem}
            {createRProjectItem}
            {importProjectItem}
          </HexMenu>
        );
      } else if (selectedTab === "components") {
        return (
          <HexMenu>
            {createComponentItem}
            {createRComponentItem}
            {importComponentItem}
          </HexMenu>
        );
      } else {
        // empty menu, we shouldn't be rendering the dropdown in this case
        return undefined;
      }
    } else {
      return (
        <HexMenu>
          {canCreateNewThread && createThreadItem}
          {createProjectItem}
          {createComponentItem}
          {createExplorationItem}
          {createCollectionItem}
          <HexMenuDivider />
          {createRProjectItem}
          {createRComponentItem}
          <HexMenuDivider />
          {importProjectItem}
          {importComponentItem}
        </HexMenu>
      );
    }
  }, [
    newProjectDisabled,
    componentsEnabled,
    placement,
    newComponentDisabled,
    createPythonComponent,
    collectionsFeatureGate,
    newCollectionDisabled,
    exploreFromScratchFeatureGate,
    newExplorationDisabled,
    createNewExplore,
    canCreateNewThread,
    createNewThread,
    createRComponent,
    onlyShowRelevantOptions,
    createPythonProject,
    openNewCollectionDialog,
    createRProject,
    openImportDialog,
    openComponentImportDialog,
    selectedTab,
    location,
  ]);

  const defaultButtonProps: HexButtonWithDropdownProps = useMemo(() => {
    return {
      hideDropdown: onlyShowRelevantOptions
        ? selectedTab !== "all" && selectedTab !== "components"
        : false,
      buttonContent: "",
      icon: <AddIcon />,
      intent: Intent.SUCCESS,
      popoverContent: popoverContents,
      small: false,
      subtle: false,
      ...buttonPropsOverrides,
    };
  }, [
    buttonPropsOverrides,
    onlyShowRelevantOptions,
    popoverContents,
    selectedTab,
  ]);

  const newProjectButton = useMemo(
    () => (
      <FeatureGateToolTip
        descriptionOverride="Unlock unlimited projects with a paid plan."
        disabled={!projectLimitEnforced}
        featureGate="projectLimit"
        icon={<ProjectsGateIcon />}
        placement={placement}
        titleOverride={`Your workspace has reached the ${projectLimit} project limit.`}
      >
        <HexButtonWithDropdown
          {...defaultButtonProps}
          buttonContent="New project"
          disabled={newProjectDisabled}
          dropdownButtonDataTest={TestSelectors.NEW_PROJECT_DROPDOWN}
          primaryButtonDataTest={TestSelectors.CREATE_PROJECT}
          tooltip={{
            combo: HotKeys.NEW_PROJECT,
            disabled: newProjectDisabled,
            hotkeyName: "Create a new project",
          }}
          // eslint-disable-next-line react/jsx-no-bind -- in useMemo
          onClick={(evt) => {
            if (location) {
              trackCounterEvent(`${location}.projects.python.create`);
            }
            void createPythonProject(undefined, isNewTabFromClickEvent(evt));
          }}
        />
      </FeatureGateToolTip>
    ),
    [
      createPythonProject,
      defaultButtonProps,
      newProjectDisabled,
      location,
      projectLimit,
      projectLimitEnforced,
      placement,
    ],
  );

  const newComponentButton = useMemo(
    () => (
      <FeatureGateToolTip
        descriptionOverride="Components give you the flexibility to reuse logic across multiple projects from a single source. Upgrade your plan to use components."
        disabled={componentsEnabled}
        featureGate="components"
        icon={<ComponentFeatureGateIcon />}
        placement={placement}
        titleOverride="Upgrade plan to enable components"
      >
        <HexButtonWithDropdown
          {...defaultButtonProps}
          buttonContent="New component"
          disabled={newComponentDisabled}
          dropdownButtonDataTest={TestSelectors.NEW_COMPONENT_DROPDOWN}
          primaryButtonDataTest={TestSelectors.CREATE_COMPONENT}
          tooltip={{
            combo: HotKeys.NEW_COMPONENT,
            disabled: newComponentDisabled,
            hotkeyName: "Create a new component",
          }}
          // eslint-disable-next-line react/jsx-no-bind -- in useMemo
          onClick={(evt) => {
            if (location) {
              trackCounterEvent(`${location}.components.python.create`);
            }
            void createPythonComponent(isNewTabFromClickEvent(evt));
          }}
        />
      </FeatureGateToolTip>
    ),
    [
      componentsEnabled,
      createPythonComponent,
      defaultButtonProps,
      newComponentDisabled,
      placement,
      location,
    ],
  );

  const newCollectionButton = useMemo(
    () => (
      <FeatureGateToolTip
        descriptionOverride="Collections give you the ability to organize and share your projects."
        disabled={collectionsFeatureGate}
        featureGate="collections"
        icon={<CollectionsGateIcon />}
        placement={placement}
        titleOverride="Upgrade plan to enable collections"
      >
        <HexButtonWithDropdown
          {...defaultButtonProps}
          buttonContent="New collection"
          disabled={newCollectionDisabled}
          tooltip={{
            combo: HotKeys.NEW_COLLECTION,
            disabled: newCollectionDisabled,
            hotkeyName: "Create a new collection",
          }}
          // eslint-disable-next-line react/jsx-no-bind -- this button rerendering should be harmless
          onClick={() => {
            if (location) {
              trackCounterEvent(`${location}.collection.create`);
            }
            openNewCollectionDialog();
          }}
        />
      </FeatureGateToolTip>
    ),
    [
      collectionsFeatureGate,
      defaultButtonProps,
      newCollectionDisabled,
      openNewCollectionDialog,
      placement,
      location,
    ],
  );

  const newExplorationButton = useMemo(
    () => (
      <FeatureGateToolTip
        action="explore outside of a published app"
        disabled={exploreFromScratchFeatureGate}
        featureGate="exploreFromScratch"
        placement={placement}
      >
        <HexButtonWithDropdown
          {...defaultButtonProps}
          buttonContent="New exploration"
          disabled={newExplorationDisabled}
          // eslint-disable-next-line react/jsx-no-bind -- in useMemo
          onClick={(evt) => {
            if (location) {
              trackCounterEvent(`${location}.explore.create`);
            }
            void createNewExplore(isNewTabFromClickEvent(evt));
          }}
        />
      </FeatureGateToolTip>
    ),
    [
      createNewExplore,
      defaultButtonProps,
      exploreFromScratchFeatureGate,
      newExplorationDisabled,
      placement,
      location,
    ],
  );

  const newThreadButton = useMemo(() => {
    return (
      <HexButtonWithDropdown
        {...defaultButtonProps}
        buttonContent="New thread"
        disabled={!canCreateNewThread}
        dropdownButtonDataTest={TestSelectors.NEW_THREAD_DROPDOWN}
        primaryButtonDataTest={TestSelectors.CREATE_THREAD}
        // eslint-disable-next-line react/jsx-no-bind -- in useMemo
        onClick={(evt) => {
          if (location) {
            trackCounterEvent(`${location}.thread.create`);
          }
          createNewThread(isNewTabFromClickEvent(evt));
        }}
      />
    );
  }, [canCreateNewThread, createNewThread, defaultButtonProps, location]);

  const {
    createNewCTA,
    disabledForRole,
    gatedAction,
    requestSource,
    role,
    tooltipOverride,
    typeOfEntity,
  } = useMemo(() => {
    switch (selectedTab) {
      case "components":
        return {
          typeOfEntity: "components",
          disabledForRole: !canEditProjectsAndCollections,
          createNewCTA: newComponentButton,
          gatedAction: OrgRoleRequestGatedAction.CREATE_COMPONENT,
          requestSource: OrgRoleRequestSource.CREATE_COMPONENT,
          role: OrgRole.EDITOR,
        };
      case "explorations":
        return {
          typeOfEntity: "explorations",
          disabledForRole: !canEditExplorations,
          createNewCTA: newExplorationButton,
          gatedAction: OrgRoleRequestGatedAction.EXPLORE,
          requestSource: OrgRoleRequestSource.CREATE_EXPLORE,
          role: exploreRoleEnabled ? OrgRole.EXPLORER : OrgRole.EDITOR,
        };
      case "collections":
        return {
          typeOfEntity: "collections",
          createNewCTA: newCollectionButton,
          disabledForRole: !canEditProjectsAndCollections,
          gatedAction: OrgRoleRequestGatedAction.CREATE_COLLECTION,
          requestSource: OrgRoleRequestSource.CREATE_COLLECTION,
          role: OrgRole.EDITOR,
        };
      case "threads":
        return {
          typeOfEntity: "threads",
          createNewCTA: newThreadButton,
          disabledForRole: !orgRoleCanCreateThreads,
          gatedAction: OrgRoleRequestGatedAction.EXPLORE,
          requestSource: OrgRoleRequestSource.CREATE_EXPLORE,
          role: exploreRoleEnabled ? OrgRole.EXPLORER : OrgRole.EDITOR,
          tooltipOverride:
            !canCreateNewThread && orgRoleCanCreateThreads ? (
              <>
                Threads is not enabled for your workspace. <ContactAnAdmin /> to
                request access.
              </>
            ) : undefined,
        };
      case "home":
        if (canEditExplorations && !canEditProjectsAndCollections) {
          if (canCreateNewThread) {
            return {
              typeOfEntity: "threads",
              createNewCTA: newThreadButton,
              disabledForRole: false,
              gatedAction: OrgRoleRequestGatedAction.EXPLORE,
              requestSource: OrgRoleRequestSource.CREATE_EXPLORE,
              role: exploreRoleEnabled ? OrgRole.EXPLORER : OrgRole.EDITOR,
            };
          } else {
            return {
              typeOfEntity: "explorations",
              disabledForRole: false,
              createNewCTA: newExplorationButton,
              gatedAction: OrgRoleRequestGatedAction.EXPLORE,
              requestSource: OrgRoleRequestSource.CREATE_EXPLORE,
              role: OrgRole.EDITOR,
            };
          }
        }
      // Fall through
      default:
        return {
          typeOfEntity: "projects",
          disabledForRole: !canEditProjectsAndCollections,
          createNewCTA: newProjectButton,
          gatedAction: OrgRoleRequestGatedAction.CREATE_PROJECT,
          requestSource: OrgRoleRequestSource.CREATE_PROJECT,
          role: OrgRole.EDITOR,
        };
    }
  }, [
    selectedTab,
    canEditProjectsAndCollections,
    newComponentButton,
    canEditExplorations,
    newExplorationButton,
    exploreRoleEnabled,
    newCollectionButton,
    newThreadButton,
    orgRoleCanCreateThreads,
    newProjectButton,
    canCreateNewThread,
  ]);

  if (disabledForRole) {
    return (
      <ButtonWrapper>
        <HexTooltip
          alignContent="center"
          content={
            <>
              <div css="text-wrap: balance; min-width: 200px;">
                You do not have permission to create {typeOfEntity}.
              </div>
              <OrgRoleRequestButton
                css={`
                  margin: 6px 0 3px;
                  width: 100%;
                `}
                gatedAction={gatedAction}
                requestSource={requestSource}
                role={role}
              />
            </>
          }
          interactionKind="hover"
          placement={placement}
        >
          {createNewCTA}
        </HexTooltip>
      </ButtonWrapper>
    );
  } else if (tooltipOverride) {
    return (
      <ButtonWrapper>
        <HexTooltip content={tooltipOverride} placement={placement}>
          {createNewCTA}
        </HexTooltip>
      </ButtonWrapper>
    );
  }

  return <ButtonWrapper>{createNewCTA}</ButtonWrapper>;
});
